/**
 * BaaS entities via MobileService `/v1/ib/console/*` → Baas (`v1/entities`).
 *
 * Default list is instance-scoped via the console API key
 * (`BaasApiInstancePerson` / `BaasApiInstanceAccounts` / global admin).
 * Optional `ownershipDocument` exists for rare document filters — Contas /
 * Criar conta virtual must NOT use it; they rely on instance links only.
 */

import {
  mapBaasEntityProposalToUi,
  resolveAccountOperationalStatus,
  resolveProposalSituation,
} from "../Utils/baasProposalSituation";
import { baasProxy, mobileConsoleRequest } from "./baasConsoleClient";
import { isVirtualInstanceShellEntity } from "./baasVirtualShells";

/** Valut matrix console pin — same intent as Contas peer-roots (AllowGlobalAccess or VALUT-GLOBAL). */
export const isValutMatrixConsolePin = (instance = {}) => {
  if (instance?.allowGlobalAccess === true || instance?.allow_global_access === true) {
    return true;
  }
  const candidates = [
    instance?.displayName,
    instance?.name,
    instance?.programId,
    instance?.programCode,
  ];
  return candidates.some((value) => {
    const normalized = String(value || "")
      .trim()
      .toUpperCase()
      .replace(/\s*\(HML\)\s*$/i, "")
      .replace(/[\s_]+/g, "-");
    if (!normalized) return false;
    if (normalized === "VALUT-GLOBAL" || normalized.startsWith("VALUT-GLOBAL-")) {
      return true;
    }
    const compact = normalized.replace(/-/g, "");
    return compact === "VALUTGLOBAL" || compact.startsWith("VALUTGLOBAL");
  });
};

export const BAAS_ENTITY_TYPES = ["pf", "pj"];
/** Conta column: Valut Account operational status only (`none` = no local Account). */
export const BAAS_ENTITY_STATUSES = ["active", "pending", "suspended", "none"];
export const BAAS_ENTITY_KYB_STATUSES = ["approved", "in_review", "pending", "rejected"];

/** Map proposal/account operational codes onto entidades Conta chip keys. */
export const normalizeEntidadeContaStatus = (status) => {
  if (status === "blocked") return "suspended";
  if (status === "active" || status === "pending" || status === "suspended" || status === "none") {
    return status;
  }
  return status || "none";
};

/**
 * Conta chip on entity detail = Valut Account operational status only.
 * Live PaySmart proposal/last must NOT drive Conta when there is no local Account.
 */
export const resolveEntidadeDetailContaStatus = ({
  entity,
  linkedAccounts = [],
  accountsResolved = false,
  liveProposalSituation = null,
} = {}) => {
  if (!entity) return "none";

  const hasLinkedAccounts = Array.isArray(linkedAccounts) && linkedAccounts.length > 0;
  // Prefer confirmed related-accounts result (avoids wrong Em análise from proposal alone).
  if (accountsResolved && !hasLinkedAccounts) {
    return "none";
  }

  const hasContaFromEntity = Boolean(
    entity.hasAccount
    || entity.documentPending
    || entity.antiFraudAnalysis
    || entity.proposalSituation
    || entity.accountId
    || (entity.raw?.status_name ?? entity.raw?.statusName),
  );

  if (!hasContaFromEntity && !hasLinkedAccounts) {
    return normalizeEntidadeContaStatus(entity.status || "none");
  }

  // Has Valut Account: Conta from Account flags / linked account status.
  // Live proposal may refine only when an Account exists.
  const primaryAccount = hasLinkedAccounts ? linkedAccounts[0] : null;
  const operational = resolveAccountOperationalStatus({
    proposalSituation:
      hasContaFromEntity || hasLinkedAccounts
        ? (liveProposalSituation || entity.proposalSituation || primaryAccount?.proposalSituation)
        : null,
    documentPending: entity.documentPending ?? primaryAccount?.documentPending,
    antiFraudAnalysis: entity.antiFraudAnalysis ?? primaryAccount?.antiFraudAnalysis,
    statusName:
      entity.raw?.status_name
      ?? entity.raw?.statusName
      ?? primaryAccount?.statusName,
    raw: {
      document_pending: entity.documentPending ?? primaryAccount?.documentPending,
      anti_fraud_analysis: entity.antiFraudAnalysis ?? primaryAccount?.antiFraudAnalysis,
      status_name:
        entity.raw?.status_name
        ?? entity.raw?.statusName
        ?? primaryAccount?.statusName,
      proposal_situation: entity.proposalSituation || primaryAccount?.proposalSituation,
    },
    ignoreEntityKycFallback: true,
  });

  if (operational.status && operational.status !== "none") {
    return normalizeEntidadeContaStatus(operational.status);
  }

  if (primaryAccount?.status) {
    return normalizeEntidadeContaStatus(primaryAccount.status);
  }

  return normalizeEntidadeContaStatus(entity.status || "pending");
};

export const DEFAULT_ENTITY_TOTALS = {
  totalTarget: 100,
  activeTarget: 80,
  pendingTarget: 10,
  suspendedTarget: 5,
};

const mapVerificationToUi = (verificationStatus) => {
  const status = String(verificationStatus || "").toUpperCase();

  if (status === "APPROVED") {
    return { status: "active", kybStatus: "approved" };
  }
  if (status === "REJECTED") {
    return { status: "suspended", kybStatus: "rejected" };
  }
  if (status === "SUSPENDED" || status === "BLOCKED") {
    return { status: "suspended", kybStatus: "pending" };
  }
  // Provider KYC (Veriff / Sumsub / etc.): UNDER_REVIEW, IN_REVIEW, IN_ANALYSIS → em análise
  if (
    status === "UNDER_REVIEW"
    || status === "IN_REVIEW"
    || status === "IN_ANALYSIS"
    || status.includes("REVIEW")
    || status.includes("ANALYSIS")
  ) {
    return { status: "pending", kybStatus: "in_review" };
  }
  if (status === "PENDING" || !status) {
    return { status: "pending", kybStatus: "pending" };
  }
  return { status: "pending", kybStatus: "pending" };
};

const mapEntityType = (type) => {
  const normalized = String(type || "").toUpperCase();
  if (normalized === "PERSON" || normalized === "PF") return "pf";
  if (normalized === "BUSINESS" || normalized === "PJ") return "pj";
  return normalized === "PF" ? "pf" : "pj";
};

const isPublicEntityId = (value) =>
  /^(enti_|pers_)/i.test(String(value || "").trim());

const firstHumanDisplayName = (...candidates) => {
  for (const candidate of candidates) {
    const trimmed = candidate == null ? "" : String(candidate).trim();
    if (!trimmed || isPublicEntityId(trimmed)) continue;
    return trimmed;
  }
  return null;
};

const resolveEntityDocument = (item) => {
  if (item?.document) return item.document;
  const bd = item?.business_details ?? item?.businessDetails;
  if (bd) {
    if (bd.ein) return bd.ein;
    if (bd.registration_id?.number) return bd.registration_id.number;
    if (bd.registrationId?.number) return bd.registrationId.number;
  }
  const pd = item?.person_details ?? item?.personDetails;
  if (pd) {
    return (
      pd.national_id?.number ||
      pd.nationalId?.number ||
      pd.passport?.number ||
      pd.drivers_license?.number ||
      pd.driversLicense?.number ||
      pd.ssn ||
      ""
    );
  }
  return "";
};

const resolveEntityDisplayName = (item) => {
  const pd = item?.person_details ?? item?.personDetails;
  const bd = item?.business_details ?? item?.businessDetails;
  const normalizedType = String(item?.type || "").toUpperCase();
  const preferBusiness =
    normalizedType === "BUSINESS"
    || normalizedType === "PJ"
    || String(item?.id || "").toLowerCase().startsWith("enti_");
  const personName = pd
    ? [pd.first_name ?? pd.firstName, pd.middle_name ?? pd.middleName, pd.last_name ?? pd.lastName]
      .filter(Boolean)
      .join(" ")
      .trim()
    : "";
  const businessFirst = [
    bd?.business_name ?? bd?.businessName,
    bd?.dba_name ?? bd?.dbaName,
    bd?.legal_name ?? bd?.legalName,
    bd?.trade_name ?? bd?.tradeName,
    item?.legal_name ?? item?.legalName,
    item?.trade_name ?? item?.tradeName,
    item?.name,
    resolveEntityDocument(item),
  ];
  const personFirst = [
    personName,
    pd?.trade_name ?? pd?.tradeName,
    pd?.legal_name ?? pd?.legalName,
    ...businessFirst,
  ];
  return firstHumanDisplayName(...(preferBusiness ? businessFirst : personFirst)) || "—";
};

const isTruthyFlag = (value) =>
  value === true || value === 1 || value === "1" || String(value).toLowerCase() === "true";

/** True when the list item has viewable upload content (FileData and/or Link). */
export const entityDocumentHasUploadedContent = (doc) => {
  if (!doc || typeof doc !== "object") return false;
  const link = doc.link ?? doc.Link;
  if (link && String(link).trim()) return true;
  if (isTruthyFlag(doc.has_file ?? doc.hasFile ?? doc.HasFile)) return true;
  return false;
};

/** Normalize entity document list items from BaaS (snake_case / camelCase / PascalCase). */
export const mapBaasEntityDocument = (doc) => {
  if (!doc || typeof doc !== "object") return null;
  const id = doc.id ?? doc.Id ?? null;
  const linkRaw = doc.link ?? doc.Link ?? null;
  const link = linkRaw && String(linkRaw).trim() ? String(linkRaw).trim() : null;
  const contentType = doc.content_type ?? doc.contentType ?? doc.ContentType ?? null;
  const fileName = doc.file_name ?? doc.fileName ?? doc.FileName ?? null;
  // Derive has_file from backend flag or Link only — do not treat mime/name alone as
  // uploaded content (metadata-only identity rows must stay out of "Documentos enviados").
  const hasFile =
    isTruthyFlag(doc.has_file ?? doc.hasFile ?? doc.HasFile) || Boolean(link);
  return {
    ...doc,
    id: id != null && id !== "" ? String(id) : null,
    document_id: doc.document_id ?? doc.documentId ?? doc.DocumentId ?? doc.description ?? null,
    entity_id: doc.entity_id ?? doc.entityId ?? doc.EntityId ?? null,
    description: doc.description ?? doc.Description ?? null,
    number: doc.number ?? doc.Number ?? null,
    country_code: doc.country_code ?? doc.countryCode ?? doc.CountryCode ?? null,
    issuing_region: doc.issuing_region ?? doc.issuingRegion ?? doc.IssuingRegion ?? null,
    expiration_date: doc.expiration_date ?? doc.expirationDate ?? doc.ExpirationDate ?? null,
    link,
    content_type: contentType,
    file_name: fileName,
    has_file: hasFile,
    created_at: doc.created_at ?? doc.createdAt ?? doc.CreatedAt ?? null,
  };
};

const mapEntityKycToUi = (kyc) => {
  if (!kyc || typeof kyc !== "object") return null;
  return {
    provider: kyc.provider ?? kyc.Provider ?? null,
    sessionId: kyc.session_id ?? kyc.sessionId ?? kyc.SessionId ?? null,
    url: kyc.url ?? kyc.Url ?? null,
    status: kyc.status ?? kyc.Status ?? null,
    accessToken: kyc.access_token ?? kyc.accessToken ?? kyc.AccessToken ?? null,
    documents: Array.isArray(kyc.documents ?? kyc.Documents)
      ? (kyc.documents ?? kyc.Documents)
      : null,
  };
};

/**
 * Align list Conta chip with detail / Contas: 0.
 * Prod (or older) list may set has_account from AccountPersonAccounts wallet
 * membership on someone else's PJ — detail uses profile.AccountId and linked
 * Conta Digital rows only. When related accounts are empty, force Sem conta.
 */
export const applyListContaLinkedAccountsGuard = (entity, linkedAccountCount) => {
  if (!entity || !entity.hasAccount) return entity;
  if (Number(linkedAccountCount) > 0) return entity;
  return {
    ...entity,
    hasAccount: false,
    accountId: null,
    status: "none",
    proposalSituation: null,
    documentPending: false,
    antiFraudAnalysis: false,
  };
};

const countLinkedAccountsForEntity = async (instance, entityId) => {
  const data = await baasProxy({
    instance,
    method: "GET",
    baasPath: "v1/accounts",
    query: { entity_id: entityId },
  });
  const items = Array.isArray(data?.items) ? data.items : [];
  return items.length;
};

/**
 * For list rows that claim Conta Digital, confirm via GET accounts?entity_id=
 * (same source as detail Contas). Skips entities already Sem conta.
 */
export const reconcileListContaWithLinkedAccounts = async (instance, entities) => {
  if (!instance || !Array.isArray(entities) || entities.length === 0) {
    return entities || [];
  }
  const candidates = entities.filter((entity) => entity?.hasAccount && entity?.id);
  if (candidates.length === 0) return entities;

  const counts = await Promise.all(
    candidates.map(async (entity) => {
      try {
        const count = await countLinkedAccountsForEntity(instance, entity.id);
        return { id: entity.id, count };
      } catch {
        // On failure keep list mapping — detail still has its own accounts fetch.
        return { id: entity.id, count: -1 };
      }
    }),
  );
  const emptyIds = new Set(
    counts.filter((row) => row.count === 0).map((row) => row.id),
  );
  if (emptyIds.size === 0) return entities;

  return entities.map((entity) =>
    emptyIds.has(entity.id)
      ? applyListContaLinkedAccountsGuard(entity, 0)
      : entity,
  );
};

export const mapBaasEntityToUi = (item, instance) => {
  const kyc = mapVerificationToUi(item?.verification_status);
  const proposalSituation = resolveProposalSituation(
    item?.proposal_situation ?? item?.proposalSituation,
    item,
  );
  const documentPending = item?.document_pending ?? item?.documentPending;
  const antiFraudAnalysis = item?.anti_fraud_analysis ?? item?.antiFraudAnalysis;
  const statusName = item?.status_name ?? item?.statusName;
  const accountIdRaw = item?.account_id ?? item?.accountId ?? item?.AccountId ?? null;
  const hasAccountRaw = item?.has_account ?? item?.hasAccount;
  // Explicit false from GET entity / fixed list must win over a stray account_id.
  const hasAccountExplicitFalse =
    hasAccountRaw === false || hasAccountRaw === 0 || hasAccountRaw === "0"
    || String(hasAccountRaw).toLowerCase() === "false";
  const hasAccountExplicit = isTruthyFlag(hasAccountRaw);
  const hasAccountId =
    accountIdRaw != null && String(accountIdRaw).trim() !== "";
  // Conta Digital only when Baas says so (has_account / account_id).
  // Wallet/console membership and entity KYC must not imply Conta Ativa / Em análise.
  const hasLocalAccount = hasAccountExplicitFalse
    ? false
    : (hasAccountExplicit || hasAccountId);
  const operational = hasLocalAccount
    ? resolveAccountOperationalStatus({
      proposalSituation,
      documentPending,
      antiFraudAnalysis,
      statusName,
      raw: {
        document_pending: documentPending,
        anti_fraud_analysis: antiFraudAnalysis,
        status_name: statusName,
        proposal_situation: item?.proposal_situation ?? item?.proposalSituation,
      },
      ignoreEntityKycFallback: true,
    })
    : { status: "none", proposalSituation: null, canTransact: false };
  // Conta: Sem conta without local Account. has_account + no pending flags → Ativa.
  let status;
  if (!hasLocalAccount) {
    status = "none";
  } else if (!operational.status || operational.status === "none") {
    status = "active";
  } else {
    status = normalizeEntidadeContaStatus(operational.status || "pending");
  }
  const kybStatus = kyc.kybStatus;
  const selectionId = instance?.id ?? null;
  const countryCode =
    item?.country_code ||
    item?.business_details?.country_of_incorporation ||
    item?.person_details?.address?.country_code ||
    null;

  return {
    id: item?.id,
    personId: Number(item?.person_id ?? item?.personId) || null,
    name: resolveEntityDisplayName(item),
    email:
      String(
        item?.person_details?.email
        || item?.personDetails?.email
        || item?.business_details?.email
        || item?.email
        || "",
      ).trim() || null,
    document: resolveEntityDocument(item),
    type: mapEntityType(item?.type),
    status,
    kybStatus,
    verificationStatus: item?.verification_status ?? item?.verificationStatus ?? null,
    kyc: mapEntityKycToUi(item?.kyc ?? item?.Kyc),
    proposalSituation: hasLocalAccount
      ? (operational.proposalSituation || proposalSituation)
      : null,
    documentPending: hasLocalAccount
      && Boolean(item?.document_pending ?? item?.documentPending ?? false),
    antiFraudAnalysis: hasLocalAccount
      && Boolean(item?.anti_fraud_analysis ?? item?.antiFraudAnalysis ?? false),
    hasAccount: hasLocalAccount,
    accountId: hasAccountId ? String(accountIdRaw).trim() : null,
    countryCode,
    instanceId: item?.instance_id ?? item?.instanceId ?? selectionId,
    instanceName: item?.instance_name ?? item?.instanceName ?? null,
    programCode: item?.program_code ?? item?.programCode ?? null,
    isPeerInstanceOwner: Boolean(
      item?.is_peer_instance_owner ?? item?.isPeerInstanceOwner,
    ),
    isInstanceOwner: Boolean(item?.is_instance_owner ?? item?.isInstanceOwner),
    createdAt: item?.created_at || null,
    balanceTotal: Number(item?.balance_total ?? item?.balanceTotal ?? 0),
    documents: Array.isArray(item?.documents ?? item?.Documents)
      ? (item.documents ?? item.Documents).map(mapBaasEntityDocument).filter(Boolean)
      : [],
    reviewReasons: Array.isArray(item?.review_reasons ?? item?.reviewReasons)
      ? (item.review_reasons ?? item.reviewReasons)
      : [],
    accessStatus: (() => {
      const raw = String(item?.access_status ?? item?.accessStatus ?? "")
        .trim()
        .toLowerCase();
      if (raw === "completed" || raw === "pending" || raw === "no_email") return raw;
      return null;
    })(),
    accessEmail: (() => {
      const raw = item?.access_email ?? item?.accessEmail ?? null;
      if (!raw || typeof raw !== "object") return null;
      return {
        realEmail: String(raw.real_email ?? raw.realEmail ?? "").trim() || null,
        aliasEmail: String(raw.alias_email ?? raw.aliasEmail ?? "").trim() || null,
        routingMode: String(raw.routing_mode ?? raw.routingMode ?? "").trim() || null,
        accountId: String(raw.account_id ?? raw.accountId ?? "").trim() || null,
      };
    })(),
    raw: item,
  };
};

const listEntitiesPage = async (
  instance,
  { page = 1, pageSize = 100, entityType, ownershipDocument, document, unscoped } = {},
) => {
  const query = { page, page_size: pageSize };
  if (entityType) {
    query.entity_type = entityType;
  }
  if (ownershipDocument) {
    query.ownership_document = ownershipDocument;
  }
  const documentDigits = String(document || "").replace(/\D/g, "").trim();
  if (documentDigits) {
    query.document = documentDigits;
  }
  // Instance owner_entity_id picker: skip X-Instance-Id pin on AllowGlobalAccess keys.
  if (unscoped === true) {
    query.unscoped = true;
  }

  return baasProxy({
    instance,
    method: "GET",
    baasPath: "v1/entities",
    query,
  });
};

/**
 * Probe whether a BUSINESS (or other) entity already exists for a tax/registration document.
 * Uses GET /v1/entities?document=&entity_type=BUSINESS (no dedicated exists endpoint).
 * @param {object} instance
 * @param {string} document CNPJ / EIN / registration digits (formatting stripped)
 * @param {{ entityType?: string }} [options]
 * @returns {Promise<{ found: boolean, entity: object|null, entities: object[] }>}
 */
export const lookupBusinessEntityByDocument = async (
  instance,
  document,
  { entityType = "BUSINESS" } = {},
) => {
  const digits = String(document || "").replace(/\D/g, "").trim();
  if (!instance || !digits) {
    return { found: false, entity: null, entities: [] };
  }

  const response = await listEntitiesPage(instance, {
    page: 1,
    pageSize: 5,
    entityType,
    document: digits,
  });
  const batch = Array.isArray(response?.items) ? response.items : [];
  const entities = batch
    .map((item) => mapBaasEntityToUi(item, instance))
    .filter((entity) => entity?.id && !isVirtualInstanceShellEntity(entity));

  return {
    found: entities.length > 0,
    entity: entities[0] || null,
    entities,
  };
};

/**
 * SERPRO autocomplete draft — `POST /v1/entities/autocomplete`.
 * Requires instance feature `serpro.autocomplete`.
 * @param {object} instance
 * @param {{ document: string, dateOfBirth?: string }} payload
 */
export const autocompleteBaasEntity = async (instance, { document, dateOfBirth } = {}) => {
  const digits = String(document || "").replace(/\D/g, "").trim();
  if (!instance || !digits) {
    throw new Error("document is required");
  }

  const body = { document: digits };
  if (dateOfBirth) {
    body.date_of_birth = String(dateOfBirth).trim();
  }

  return baasProxy({
    instance,
    method: "POST",
    baasPath: "v1/entities/autocomplete",
    body,
  });
};

/**
 * Valut matrix only: GET `v1/entities/peer-owners` — OwnerEntityId of other
 * instances (labeled with instance_name). Empty for partners / non-matrix pins.
 * Does not re-link AEPs onto VALUT-GLOBAL.
 *
 * @param {object} instance
 */
export const fetchBaasPeerInstanceOwners = async (instance) => {
  if (!instance || !isValutMatrixConsolePin(instance)) {
    return [];
  }

  try {
    const data = await baasProxy({
      instance,
      method: "GET",
      baasPath: "v1/entities/peer-owners",
    });
    const items = Array.isArray(data?.items) ? data.items : [];
    return items
      .map((item) => {
        const mapped = mapBaasEntityToUi(item, instance);
        const instanceName = item?.instance_name || item?.instanceName || "";
        if (instanceName) mapped.instanceName = instanceName;
        mapped.isPeerInstanceOwner = true;
        mapped.isInstanceOwner = true;
        return mapped;
      })
      .filter((entity) => entity?.id && !isVirtualInstanceShellEntity(entity));
  } catch (error) {
    if (typeof console !== "undefined" && console.warn) {
      console.warn("[baas] GET /v1/entities/peer-owners failed", error);
    }
    return [];
  }
};

export const fetchBaasEntities = async (instance, options = {}) => {
  if (!instance) return [];

  const pageSize = options.pageSize ?? 100;
  let page = 1;
  let total = Infinity;
  const items = [];
  const ownershipDocument = String(options.ownershipDocument || "").trim();

  const documentFilter = String(options.document || "").replace(/\D/g, "").trim();

  while (items.length < total && page < 50) {
    const response = await listEntitiesPage(instance, {
      page,
      pageSize,
      entityType: options.entityType,
      ownershipDocument: ownershipDocument || undefined,
      document: documentFilter || undefined,
      unscoped: options.unscoped === true,
    });
    const batch = Array.isArray(response?.items) ? response.items : [];
    total = Number(response?.total ?? batch.length);
    items.push(...batch);
    if (batch.length < pageSize) break;
    page += 1;
  }

  const mapped = items
    .map((item) => mapBaasEntityToUi(item, instance))
    .filter((entity) => !isVirtualInstanceShellEntity(entity));

  // Opt-in: entidades list Conta must match detail Contas: 0. Contas page skips
  // this (it already loads accounts and would double-fetch).
  if (options.reconcileContaWithAccounts === true) {
    return reconcileListContaWithLinkedAccounts(instance, mapped);
  }
  return mapped;
};

/**
 * One Baas page for the entidades grid. Does not walk `total`.
 * Pickers that need every row still use {@link fetchBaasEntities}.
 */
export const fetchBaasEntitiesPage = async (instance, options = {}) => {
  if (!instance) {
    return { items: [], total: 0, page: 1, pageSize: options.pageSize ?? 20 };
  }

  const page = Math.max(1, Number(options.page) || 1);
  const pageSize = Math.min(100, Math.max(1, Number(options.pageSize) || 20));
  const ownershipDocument = String(options.ownershipDocument || "").trim();
  const documentFilter = String(options.document || "").replace(/\D/g, "").trim();

  const response = await listEntitiesPage(instance, {
    page,
    pageSize,
    entityType: options.entityType,
    ownershipDocument: ownershipDocument || undefined,
    document: documentFilter || undefined,
    unscoped: options.unscoped === true,
  });
  const batch = Array.isArray(response?.items) ? response.items : [];
  let items = batch
    .map((item) => mapBaasEntityToUi(item, instance))
    .filter((entity) => !isVirtualInstanceShellEntity(entity));

  if (options.reconcileContaWithAccounts === true) {
    items = await reconcileListContaWithLinkedAccounts(instance, items);
  }

  const total = Number(response?.total ?? items.length);
  return {
    items,
    total: Number.isFinite(total) ? total : items.length,
    page: Number(response?.page ?? page) || page,
    pageSize: Number(response?.page_size ?? pageSize) || pageSize,
  };
};

/** @deprecated Use fetchBaasEntities — sync mock removed. */
export const getMockBaasEntities = (instanceId = null) => {
  void instanceId;
  return [];
};

export const getMockBaasEntityById = (entityId, instanceId = null) => {
  void instanceId;
  return entityId ? { id: entityId } : null;
};

export const fetchBaasEntityById = async (instance, entityId) => {
  if (!instance || !entityId) return null;

  const data = await baasProxy({
    instance,
    method: "GET",
    baasPath: `v1/entities/${encodeURIComponent(entityId)}`,
  });

  return mapBaasEntityToUi(data, instance);
};

/**
 * Re-send user-access / primeiro acesso invite for a PERSON entity.
 * `POST v1/entities/{entity_id}/access-email/resend` (scope entities.person.create).
 */
export const resendBaasEntityAccessEmail = async (instance, entityId) => {
  if (!instance || !entityId) {
    const err = new Error("instance and entityId are required");
    err.name = "BaasConsoleError";
    err.code = "validation_error";
    throw err;
  }

  return baasProxy({
    instance,
    method: "POST",
    baasPath: `v1/entities/${encodeURIComponent(entityId)}/access-email/resend`,
  });
};

/** Session cache for soft proposal/last reads (keyed by instance + entity). */
const proposalSessionCache = new Map();

const proposalCacheKey = (instance, entityId) => {
  const instanceKey =
    instance?.id
    ?? instance?.instanceId
    ?? instance?.baasApiInstanceId
    ?? instance?.slug
    ?? "default";
  return `${String(instanceKey)}:${String(entityId || "").trim()}`;
};

export const clearBaasEntityProposalSessionCache = (instance = null, entityId = null) => {
  if (!instance && !entityId) {
    proposalSessionCache.clear();
    return;
  }
  if (instance && entityId) {
    proposalSessionCache.delete(proposalCacheKey(instance, entityId));
    return;
  }
  const prefix = instance
    ? `${String(
      instance?.id
        ?? instance?.instanceId
        ?? instance?.baasApiInstanceId
        ?? instance?.slug
        ?? "default",
    )}:`
    : null;
  if (!prefix) return;
  for (const key of proposalSessionCache.keys()) {
    if (key.startsWith(prefix)) proposalSessionCache.delete(key);
  }
};

/**
 * Latest Conta Digital proposal for the entity
 * (`GET /v1/entities/{entity_id}/proposal`). Only meaningful after open-account
 * (local Account + proposal_id). Soft-fails with null when Baas returns 404
 * (no account/proposal yet) or the partner call is unavailable — callers should
 * skip this until `has_account` / linked accounts exist.
 *
 * Console first-load / enter must NOT call this — Contas/Entidades use local
 * `proposalSituation` only. Live GET is {@link consultBaasEntityProposal}
 * (Consultar proposta). Pass `{ force: true }` for an explicit refresh.
 */
export const fetchBaasEntityProposal = async (instance, entityId, { force = false } = {}) => {
  if (!instance || !entityId) return null;
  const key = proposalCacheKey(instance, entityId);
  if (!force && proposalSessionCache.has(key)) {
    return proposalSessionCache.get(key);
  }
  try {
    const next = await consultBaasEntityProposal(instance, entityId, { force: true });
    proposalSessionCache.set(key, next);
    return next;
  } catch {
    if (!force && proposalSessionCache.has(key)) {
      return proposalSessionCache.get(key);
    }
    return null;
  }
};

/**
 * Same as {@link fetchBaasEntityProposal} but surfaces Baas/PaySmart errors
 * (used by Contas "Consultar proposta" so ops see deserialize / partner failures).
 * Always hits Baas/PaySmart unless `{ force: false }` and a session cache entry exists.
 */
export const consultBaasEntityProposal = async (instance, entityId, { force = true } = {}) => {
  if (!instance || !entityId) {
    const err = new Error("Instance and entityId are required");
    err.name = "BaasConsoleError";
    err.code = "proposal_consult_invalid";
    throw err;
  }

  const key = proposalCacheKey(instance, entityId);
  if (!force && proposalSessionCache.has(key)) {
    return proposalSessionCache.get(key);
  }

  const data = await baasProxy({
    instance,
    method: "GET",
    baasPath: `v1/entities/${encodeURIComponent(entityId)}/proposal`,
  });
  const mapped = mapBaasEntityProposalToUi(data);
  proposalSessionCache.set(key, mapped);
  return mapped;
};

/**
 * Prefer Baas `acct_…` id for Conta Digital proposal routes.
 */
export const resolveBaasAccountExternalId = (account) => {
  if (!account) return null;
  const candidates = [
    account.banking?.accountId,
    account.metadata?.externalAccountId,
    account.id,
    account.accountNumber,
  ]
    .map((value) => (value != null ? String(value).trim() : ""))
    .filter(Boolean);
  const preferred = candidates.find((id) => /^acct_/i.test(id));
  return preferred || candidates[0] || null;
};

/**
 * Valut global admin only: manually approve entity verification
 * (`POST /v1/entities/{entity_id}/verification/approve`).
 * Does not require documents or skip-KYC. Does not open a bank account.
 */
export const approveBaasEntityVerification = async (instance, entityId) => {
  if (!instance || !entityId) {
    throw new Error("Instance and entityId are required");
  }

  return baasProxy({
    instance,
    method: "POST",
    baasPath: `v1/entities/${encodeURIComponent(entityId)}/verification/approve`,
    body: {},
  });
};

/**
 * Manual Conta Digital proposal approval via PaySmart
 * (`PUT /v1/accounts/{account_id}/proposals`, entity_id in body).
 * Nested `PUT /v1/entities/{entity_id}/accounts/{account_id}/proposals` remains supported by Baas.
 * Requires instance `allow_skip_kyc` / AllowSkipKycLink, or Valut platform admin
 * (support path after Veriff/proposal reject).
 */
export const approveBaasAccountProposal = async (
  instance,
  entityId,
  accountId,
  { proposalId, proposalStatus = "MANUAL_APPROVED" } = {},
) => {
  if (!instance || !entityId || !accountId) {
    throw new Error("Instance, entityId and accountId are required");
  }

  const body = {
    proposal_status: proposalStatus || "MANUAL_APPROVED",
    entity_id: entityId,
  };
  if (proposalId != null && Number(proposalId) > 0) {
    body.proposal_id = Number(proposalId);
  }

  const data = await baasProxy({
    instance,
    method: "PUT",
    baasPath: `v1/accounts/${encodeURIComponent(accountId)}/proposals`,
    body,
  });
  const mapped = mapBaasEntityProposalToUi(data);
  clearBaasEntityProposalSessionCache(instance, entityId);
  if (mapped) {
    proposalSessionCache.set(proposalCacheKey(instance, entityId), mapped);
  }
  return mapped;
};

/**
 * Updates editable contact + BUSINESS profile fields via BaaS
 * `PATCH /v1/entities/{entity_id}`.
 * Name and identity documents are not accepted by the API.
 */
export const updateBaasEntityContact = async (
  instance,
  entityId,
  {
    phoneNumber,
    phone,
    email,
    address,
    country_of_incorporation,
    state_of_incorporation,
    legal_type,
    industry,
    date_of_incorporation,
    year_of_incorporation,
    has_virtual_address,
    expected_payment_rails,
    purpose_of_account,
    description,
    website,
    countries_of_operation,
    account_usage,
    payment_volumes,
    source_of_funds,
    expected_use_of_funds,
    end_customer_type,
    makes_international_payments,
    is_high_risk,
    ein_pending,
    registration_id,
    first_name,
    middle_name,
    last_name,
    date_of_birth,
    mother_name,
    legal_name,
  } = {},
) => {
  if (!instance || !entityId) {
    throw new Error("Instance and entityId are required");
  }

  const body = {};
  if (phone && typeof phone === "object") {
    const country = String(phone.countryDialCode ?? phone.country_dial_code ?? "").replace(/\D/g, "");
    const area = String(phone.areaCode ?? phone.area_code ?? "").replace(/\D/g, "");
    const number = String(phone.number ?? "").replace(/\D/g, "");
    if (country && area && number) {
      body.phone = {
        country_dial_code: country,
        area_code: area,
        number,
      };
      body.phone_number = `+${country}${area}${number}`;
    }
  } else if (phoneNumber != null && String(phoneNumber).trim() !== "") {
    body.phone_number = String(phoneNumber).trim();
  }
  if (email !== undefined) {
    body.email = email == null || String(email).trim() === "" ? null : String(email).trim();
  }
  if (address) {
    body.address = {
      line_1: address.line1?.trim() || address.line_1?.trim() || "",
      line_2: address.line2?.trim() || address.line_2?.trim() || null,
      number: address.number?.trim() || null,
      neighborhood: address.neighborhood?.trim() || null,
      city: address.city?.trim() || "",
      state: address.state?.trim() || null,
      postal_code: address.postalCode?.trim() || address.postal_code?.trim() || null,
      country_code: (address.countryCode || address.country_code || "").trim().toUpperCase(),
    };
  }

  const assignIfPresent = (key, value) => {
    if (value !== undefined) {
      body[key] = value;
    }
  };
  assignIfPresent("country_of_incorporation", country_of_incorporation);
  assignIfPresent("state_of_incorporation", state_of_incorporation);
  assignIfPresent("legal_type", legal_type);
  assignIfPresent("industry", industry);
  assignIfPresent("date_of_incorporation", date_of_incorporation);
  assignIfPresent("year_of_incorporation", year_of_incorporation);
  assignIfPresent("has_virtual_address", has_virtual_address);
  assignIfPresent("expected_payment_rails", expected_payment_rails);
  assignIfPresent("purpose_of_account", purpose_of_account);
  assignIfPresent("description", description);
  assignIfPresent("website", website);
  assignIfPresent("countries_of_operation", countries_of_operation);
  assignIfPresent("account_usage", account_usage);
  assignIfPresent("payment_volumes", payment_volumes);
  assignIfPresent("source_of_funds", source_of_funds);
  assignIfPresent("expected_use_of_funds", expected_use_of_funds);
  assignIfPresent("end_customer_type", end_customer_type);
  assignIfPresent("makes_international_payments", makes_international_payments);
  assignIfPresent("is_high_risk", is_high_risk);
  assignIfPresent("ein_pending", ein_pending);
  assignIfPresent("registration_id", registration_id);
  assignIfPresent("first_name", first_name);
  assignIfPresent("middle_name", middle_name);
  assignIfPresent("last_name", last_name);
  assignIfPresent("date_of_birth", date_of_birth);
  assignIfPresent("mother_name", mother_name);
  assignIfPresent("legal_name", legal_name);

  const data = await baasProxy({
    instance,
    method: "PATCH",
    baasPath: `v1/entities/${encodeURIComponent(entityId)}`,
    body,
  });

  return mapBaasEntityToUi(data, instance);
};

/**
 * Adds beneficial owners to an existing BUSINESS via BaaS
 * `POST /v1/entities/{entity_id}/beneficial-owners`.
 * Does not replace owners already linked.
 */
export const addBaasEntityBeneficialOwners = async (
  instance,
  entityId,
  beneficialOwners = [],
) => {
  if (!instance || !entityId) {
    throw new Error("Instance and entityId are required");
  }

  const data = await baasProxy({
    instance,
    method: "POST",
    baasPath: `v1/entities/${encodeURIComponent(entityId)}/beneficial-owners`,
    body: {
      beneficial_owners: beneficialOwners,
    },
  });

  return mapBaasEntityToUi(data, instance);
};

/**
 * Uploads document files for an entity when the instance allows skip-KYC
 * (`AllowSkipKycLink`). BaaS `POST /v1/entities/{id}/documents`.
 */
export const uploadBaasEntityDocuments = async (instance, entityId, documents = []) => {
  if (!instance || !entityId) {
    throw new Error("Instance and entityId are required");
  }

  return baasProxy({
    instance,
    method: "POST",
    baasPath: `v1/entities/${encodeURIComponent(entityId)}/documents`,
    body: { documents },
  });
};

/**
 * Valut admin only: pull Veriff decision by session id and sync KYC metadata (+ media best-effort).
 * BaaS `POST /v1/entities/{id}/kyc/veriff/sync`.
 */
export const syncBaasEntityVeriffSession = async (instance, entityId, sessionId) => {
  if (!instance || !entityId) {
    throw new Error("Instance and entityId are required");
  }
  const trimmed = String(sessionId || "").trim();
  if (!trimmed) {
    throw new Error("session_id is required");
  }

  return baasProxy({
    instance,
    method: "POST",
    baasPath: `v1/entities/${encodeURIComponent(entityId)}/kyc/veriff/sync`,
    body: { session_id: trimmed },
  });
};

/**
 * Valut matrix admin only: re-POST PaySmart save-documents for the person or every PJ sócio.
 * Does not open a Conta Digital account. Console UI is matrix-card + isValutConfigAdmin only.
 * BaaS `POST /v1/entities/{id}/kyc/paysmart/documents`.
 */
export const pushBaasEntityPaySmartDocuments = async (instance, entityId) => {
  if (!instance || !entityId) {
    throw new Error("Instance and entityId are required");
  }

  return baasProxy({
    instance,
    method: "POST",
    baasPath: `v1/entities/${encodeURIComponent(entityId)}/kyc/paysmart/documents`,
  });
};

/**
 * Create a Veriff KYC session for an existing person entity (or return existing).
 * Requires instance feature `kyc.veriff_session_create`.
 * Clients also need Skip KYC off; Valut admins may create when the feature is on (support).
 * BaaS `POST /v1/entities/{id}/kyc/veriff/session`.
 */
export const createBaasEntityVeriffSession = async (instance, entityId) => {
  if (!instance || !entityId) {
    throw new Error("Instance and entityId are required");
  }

  return baasProxy({
    instance,
    method: "POST",
    baasPath: `v1/entities/${encodeURIComponent(entityId)}/kyc/veriff/session`,
  });
};

/**
 * Stub hook: send Veriff session URL by email. Template is not wired yet.
 * BaaS `POST /v1/entities/{id}/kyc/veriff/session/email`.
 */
export const sendBaasEntityVeriffSessionEmail = async (
  instance,
  entityId,
  { sessionUrl, sessionId, email } = {},
) => {
  if (!instance || !entityId) {
    throw new Error("Instance and entityId are required");
  }

  return baasProxy({
    instance,
    method: "POST",
    baasPath: `v1/entities/${encodeURIComponent(entityId)}/kyc/veriff/session/email`,
    body: {
      session_url: sessionUrl || undefined,
      session_id: sessionId || undefined,
      email: email || undefined,
    },
  });
};

/**
 * Create a blank KYC onboard invite (no PERSON yet).
 * BaaS `POST /v1/kyc/invites` (feature `kyc.invite_link`).
 */
export const createBaasKycOnboardInvite = async (instance, { expiresInHours, name } = {}) => {
  if (!instance) {
    throw new Error("Instance is required");
  }

  const body = {};
  if (expiresInHours != null && Number.isFinite(Number(expiresInHours))) {
    body.expires_in_hours = Number(expiresInHours);
  }
  const trimmedName = typeof name === "string" ? name.trim() : "";
  if (trimmedName) {
    body.name = trimmedName;
  }

  return baasProxy({
    instance,
    method: "POST",
    baasPath: "v1/kyc/invites",
    body,
  });
};

/**
 * List all KYC onboard invites for the current instance.
 * BaaS `GET /v1/kyc/invites`.
 */
export const listBaasKycOnboardInvites = async (instance) => {
  if (!instance) {
    throw new Error("Instance is required");
  }

  const data = await baasProxy({
    instance,
    method: "GET",
    baasPath: "v1/kyc/invites",
  });
  const invites = data?.invites ?? data?.Invites ?? data;
  return Array.isArray(invites) ? invites : [];
};

/**
 * Revoke an instance-scoped KYC onboard invite.
 * BaaS `POST /v1/kyc/invites/{inviteId}/revoke`.
 */
export const revokeBaasKycOnboardInvite = async (instance, inviteId) => {
  if (!instance || inviteId == null) {
    throw new Error("Instance and inviteId are required");
  }

  return baasProxy({
    instance,
    method: "POST",
    baasPath: `v1/kyc/invites/${encodeURIComponent(inviteId)}/revoke`,
  });
};

/**
 * Create KYC invite link for an existing PERSON entity (re-invite).
 * BaaS `POST /v1/entities/{id}/kyc/invites` (feature `kyc.invite_link`).
 */
export const createBaasEntityKycInvite = async (
  instance,
  entityId,
  { expiresInHours, updateScopes, includeLiveness } = {},
) => {
  if (!instance || !entityId) {
    throw new Error("Instance and entityId are required");
  }

  const body = {};
  if (expiresInHours != null && Number.isFinite(Number(expiresInHours))) {
    body.expires_in_hours = Number(expiresInHours);
  }
  if (Array.isArray(updateScopes)) {
    body.update_scopes = updateScopes;
  }
  if (typeof includeLiveness === "boolean") {
    body.include_liveness = includeLiveness;
  }

  return baasProxy({
    instance,
    method: "POST",
    baasPath: `v1/entities/${encodeURIComponent(entityId)}/kyc/invites`,
    body,
  });
};

/**
 * List KYC invite links for a PERSON entity.
 * BaaS `GET /v1/entities/{id}/kyc/invites`.
 */
export const listBaasEntityKycInvites = async (instance, entityId) => {
  if (!instance || !entityId) {
    throw new Error("Instance and entityId are required");
  }

  const data = await baasProxy({
    instance,
    method: "GET",
    baasPath: `v1/entities/${encodeURIComponent(entityId)}/kyc/invites`,
  });
  const invites = data?.invites ?? data?.Invites ?? data;
  return Array.isArray(invites) ? invites : [];
};

/**
 * Revoke a KYC invite link.
 * BaaS `POST /v1/entities/{id}/kyc/invites/{inviteId}/revoke`.
 */
export const revokeBaasEntityKycInvite = async (instance, entityId, inviteId) => {
  if (!instance || !entityId || inviteId == null) {
    throw new Error("Instance, entityId and inviteId are required");
  }

  return baasProxy({
    instance,
    method: "POST",
    baasPath: `v1/entities/${encodeURIComponent(entityId)}/kyc/invites/${encodeURIComponent(inviteId)}/revoke`,
  });
};

/**
 * Fetches document content for console preview.
 * Prefers Valut `file_base64` (stored FileData). `link` is only present when no file was stored.
 * BaaS `GET /v1/entities/{id}/documents/{documentId}`.
 */
export const fetchBaasEntityDocumentContent = async (instance, entityId, documentId) => {
  if (!instance || !entityId || documentId == null || documentId === "") {
    throw new Error("Instance, entityId and documentId are required");
  }

  return baasProxy({
    instance,
    method: "GET",
    baasPath: `v1/entities/${encodeURIComponent(entityId)}/documents/${encodeURIComponent(documentId)}`,
  });
};

/**
 * Clears uploaded proof content (FileData / Link) for a document.
 * BaaS `DELETE /v1/entities/{id}/documents/{documentId}`.
 */
export const deleteBaasEntityDocument = async (instance, entityId, documentId) => {
  if (!instance || !entityId || documentId == null || documentId === "") {
    throw new Error("Instance, entityId and documentId are required");
  }

  return baasProxy({
    instance,
    method: "DELETE",
    baasPath: `v1/entities/${encodeURIComponent(entityId)}/documents/${encodeURIComponent(documentId)}`,
  });
};

/**
 * Updates editable document declaration fields (number / country / issuing region / validity).
 * BaaS `PATCH /v1/entities/{id}/documents/{documentId}`.
 */
export const updateBaasEntityDocument = async (
  instance,
  entityId,
  documentId,
  { number, countryCode, issuingRegion, expirationDate } = {},
) => {
  if (!instance || !entityId || documentId == null || documentId === "") {
    throw new Error("Instance, entityId and documentId are required");
  }

  const body = {};
  if (number !== undefined) {
    body.number = number == null ? "" : String(number).trim();
  }
  if (countryCode !== undefined) {
    body.country_code =
      countryCode == null || String(countryCode).trim() === ""
        ? ""
        : String(countryCode).trim().toUpperCase();
  }
  if (issuingRegion !== undefined) {
    body.issuing_region =
      issuingRegion == null || String(issuingRegion).trim() === ""
        ? ""
        : String(issuingRegion).trim();
  }
  if (expirationDate !== undefined) {
    body.expiration_date =
      expirationDate == null || String(expirationDate).trim() === ""
        ? ""
        : String(expirationDate).trim();
  }

  const data = await baasProxy({
    instance,
    method: "PATCH",
    baasPath: `v1/entities/${encodeURIComponent(entityId)}/documents/${encodeURIComponent(documentId)}`,
    body,
  });

  return mapBaasEntityDocument(data);
};

/**
 * Creates or updates a declared identity/tax document by type.
 * BaaS `PUT /v1/entities/{id}/documents/declarations`.
 */
export const upsertBaasEntityDocumentDeclaration = async (
  instance,
  entityId,
  { type, number, countryCode, issuingRegion, expirationDate } = {},
) => {
  if (!instance || !entityId || !type) {
    throw new Error("Instance, entityId and type are required");
  }

  const body = { type: String(type).trim().toUpperCase() };
  if (number !== undefined) {
    body.number = number == null ? "" : String(number).trim();
  }
  if (countryCode !== undefined) {
    body.country_code =
      countryCode == null || String(countryCode).trim() === ""
        ? ""
        : String(countryCode).trim().toUpperCase();
  }
  if (issuingRegion !== undefined) {
    body.issuing_region =
      issuingRegion == null || String(issuingRegion).trim() === ""
        ? ""
        : String(issuingRegion).trim();
  }
  if (expirationDate !== undefined) {
    body.expiration_date =
      expirationDate == null || String(expirationDate).trim() === ""
        ? ""
        : String(expirationDate).trim();
  }

  const data = await baasProxy({
    instance,
    method: "PUT",
    baasPath: `v1/entities/${encodeURIComponent(entityId)}/documents/declarations`,
    body,
  });

  return mapBaasEntityDocument(data);
};

/**
 * Local UI patch only — prefer updateBaasEntityContact for portal edits.
 * @deprecated
 */
export const updateMockBaasEntity = (entityId, patch = {}, instanceId = null) => {
  void instanceId;
  if (!entityId) return null;
  return { id: entityId, ...patch };
};

export const createMockBaasEntity = (instanceId, data = {}) => {
  const id = data.id || `ent-${Date.now()}`;
  return {
    id,
    name: data.name ?? "",
    document: data.document ?? "",
    type: data.type ?? "pj",
    status: data.status ?? "pending",
    kybStatus: data.kybStatus ?? "pending",
    instanceId,
    createdAt: new Date().toISOString(),
    balanceTotal: data.balanceTotal ?? 0,
    ...(data.personalAccount ? { personalAccount: data.personalAccount } : {}),
  };
};

export const computeEntityTotals = (entities = []) => ({
  total: entities.length,
  active: entities.filter((entity) => entity.status === "active").length,
  pending: entities.filter((entity) => entity.status === "pending").length,
  suspended: entities.filter((entity) => entity.status === "suspended").length,
  pendingKyb: entities.filter((entity) =>
    ["pending", "in_review"].includes(entity.kybStatus),
  ).length,
});

export const fetchBaasCarteiraPresence = async (personIds = []) => {
  const ids = [...new Set(personIds.map((id) => Number(id)).filter((id) => id > 0))];
  if (ids.length === 0) return new Set();

  const data = await mobileConsoleRequest({
    method: "POST",
    path: "v1/ib/baas/sessions/presence",
    body: { person_ids: ids },
  });
  const list = Array.isArray(data?.logged_in_person_ids)
    ? data.logged_in_person_ids
    : Array.isArray(data?.loggedInPersonIds)
      ? data.loggedInPersonIds
      : [];
  return new Set(list.map((id) => Number(id)).filter((id) => id > 0));
};

export const revokeBaasCarteiraSession = async (personId) => {
  const numeric = Number(personId);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    const err = new Error("personId is required");
    err.name = "BaasConsoleError";
    err.code = "validation_error";
    throw err;
  }

  return mobileConsoleRequest({
    method: "POST",
    path: `v1/ib/baas/persons/${numeric}/sessions/revoke`,
  });
};

/**
 * Valut matrix admin: wipe correntista transactional PIN (carteira).
 * `DELETE v1/ib/baas/persons/{personId}/transactional-password`
 */
export const resetBaasEntityTransactionalPassword = async (personId) => {
  const numeric = Number(personId);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    const err = new Error("personId is required");
    err.name = "BaasConsoleError";
    err.code = "validation_error";
    throw err;
  }

  return mobileConsoleRequest({
    method: "DELETE",
    path: `v1/ib/baas/persons/${numeric}/transactional-password`,
  });
};

export const deactivateBaasEntity = async (instance, entityId, { retainBalance = false } = {}) => {
  if (!instance || !entityId) {
    const err = new Error("instance and entityId are required");
    err.name = "BaasConsoleError";
    err.code = "validation_error";
    throw err;
  }

  return baasProxy({
    instance,
    method: "POST",
    baasPath: `v1/entities/${encodeURIComponent(entityId)}/deactivate`,
    body: { retain_balance: Boolean(retainBalance) },
  });
};

/**
 * Reverse of {@link deactivateBaasEntity}: reactivate the entity and linked accounts.
 * `POST v1/entities/{entity_id}/activate`.
 */
export const activateBaasEntity = async (instance, entityId) => {
  if (!instance || !entityId) {
    const err = new Error("instance and entityId are required");
    err.name = "BaasConsoleError";
    err.code = "validation_error";
    throw err;
  }

  return baasProxy({
    instance,
    method: "POST",
    baasPath: `v1/entities/${encodeURIComponent(entityId)}/activate`,
  });
};

const INACTIVE_ACCOUNT_LIFECYCLES = new Set(["deactivated", "suspended", "blocked"]);

const resolveLinkedAccountLifecycle = (account) =>
  String(account?.lifecycle || account?.status || "").trim().toLowerCase();

/** Conta chip is blocked / suspended / deactivated → Ativar, never Desativar. */
export const isEntidadeContaInactive = (status) =>
  INACTIVE_ACCOUNT_LIFECYCLES.has(String(status || "").trim().toLowerCase());

/** Acesso chip "Aguardando primeiro acesso" (`access_status=pending`). */
export const isBaasEntityAwaitingFirstAccess = (entityOrStatus) => {
  const raw = typeof entityOrStatus === "string" || entityOrStatus == null
    ? entityOrStatus
    : entityOrStatus?.accessStatus;
  return String(raw ?? "").trim().toLowerCase() === "pending";
};

/**
 * Admin deactivate sets verification_status SUSPENDED.
 * Ativar only when there is no operable Conta Digital (all linked accounts
 * deactivated/suspended/blocked). An active/pending account wins over a sibling
 * deactivated row and over a stale SUSPENDED KYC flag.
 */
export const isBaasEntityDeactivated = (entity, linkedAccounts = []) => {
  const accounts = Array.isArray(linkedAccounts) ? linkedAccounts : [];
  if (accounts.length > 0) {
    return accounts.every((account) =>
      INACTIVE_ACCOUNT_LIFECYCLES.has(resolveLinkedAccountLifecycle(account)),
    );
  }
  const verification = String(entity?.verificationStatus || "").toUpperCase();
  return verification === "SUSPENDED" || verification === "BLOCKED";
};
